"use client"

import { useEffect, useId, useState } from "react"

import { MarketingButton, MarketingLink, marketingActionClass } from "@/components/marketing/marketing-action"
import { marketingFieldClass } from "@/components/marketing/marketing-field"
import { MarketingAlert } from "@/components/marketing/marketing-alert"
import { useInquiryForm } from "@/components/marketing/use-inquiry-form"
import { HoneypotField } from "@/components/marketing/honeypot-field"
import { readAcquisitionContext } from "@/lib/acquisition/context"
import { withHoneypot } from "@/lib/honeypot"
import { SITE_CONFIG } from "@/lib/site-config"
import { normalizeEmailAddress } from "@/lib/utils"

import { formatAmount, renewsQueryFromUrl, weekendQueryFromUrl } from "./checkout-shared"

// The way to still buy when checkout cannot start. It renders only where
// /pay/embedded would otherwise leave a buyer at a dead end (the checkout call
// answered 503 "temporarily unavailable" or failed any other way), never beside
// a working payment form. It takes no card details and creates no payment
// record: the form posts name, email and an optional note to /api/contact, the
// existing persist-first intake (lib/forms/inbound.ts stores it in
// inbound_messages first, then notifies the shared inbox), with the chosen
// plan or product written into the stored message.
//
// Every new visible string lives in FALLBACK_COPY so they can be approved at
// once. The business email and phone come from SITE_CONFIG, the single source.

export const FALLBACK_COPY = {
  heading: "Let's finish your order",
  lead: "Your payment couldn't be completed on this page. Leave your details below, and we'll help you complete your purchase.",
  nameLabel: "Name",
  emailLabel: "Email",
  noteLabel: "Anything we should know? (optional)",
  submit: "Send my details",
  sending: "Sending…",
  sent: "Thanks, we have your details and will be in touch to finish your order.",
  failed: "We couldn't send that. Please email or text us instead.",
  order: "Your order:",
  directLead: "Prefer to reach us directly?",
  emailWord: "Email",
  textWord: "text",
  or: "or",
  textOnly: "Or text us at",
} as const

/** Whether a checkout answer means "checkout cannot take this payment right
 *  now". A sign-in request (401) and deliberate refusals (409 already owns
 *  it, 410 no longer offered) are real answers about this buyer or plan, not
 *  an outage, so they never get the panel. Everything else does: 5xx, 429, a
 *  network failure (no status) and the ambiguous 400s. */
export function checkoutIsDown(httpStatus: number | undefined): boolean {
  return httpStatus !== 401 && httpStatus !== 409 && httpStatus !== 410
}

/** "+19292097870" shown as "+1 929-209-7870". Derived from the one stored
 *  number so the display can never drift from the link. */
function displayPhone(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164)
  return m ? `+1 ${m[1]}-${m[2]}-${m[3]}` : e164
}

/** What the buyer was trying to buy, in words the inbox can act on. */
export function describeChoice({
  offerTitle,
  plan,
  price,
  amount,
}: {
  offerTitle: string | null
  plan: string | null
  price: string | null
  amount: string | null
}): string {
  const parts: string[] = []
  if (offerTitle) parts.push(offerTitle)
  if (plan) parts.push(`plan ${plan}`)
  if (price) parts.push(`price ${price}`)
  if (amount) parts.push(`custom amount $${amount}`)
  return parts.length > 0 ? parts.join(", ") : "not specified"
}

const LINK_CLASS =
  "font-medium text-[var(--color-vermilion)] underline underline-offset-2 transition-colors hover:text-[var(--color-oxblood)]"

function DetailsFallback({
  offerTitle,
  plan,
  price,
  amount,
  showEmail = true,
  sent = false,
  onSent,
  prefillEmail,
}: {
  offerTitle: string | null
  plan: string | null
  price: string | null
  amount: string | null
  /** False where the checkout notice above already prints the email address. */
  showEmail?: boolean
  /** Kept by the parent so the confirmation survives a remount. */
  sent?: boolean
  onSent?: () => void
  prefillEmail?: string | null
}) {
  const headingId = useId()
  const [name, setName] = useState("")
  const [email, setEmail] = useState(prefillEmail ?? "")
  const [note, setNote] = useState("")
  const { state, error, submit } = useInquiryForm({
    endpoint: "/api/contact",
    genericErrorCopy: FALLBACK_COPY.failed,
  })

  useEffect(() => {
    if (state === "ok") onSent?.()
  }, [state, onSent])

  const done = sent || state === "ok"
  const orderLine = offerTitle ?? (amount ? `$${amount}` : null)
  // The hook shows the server's own error text, which is not ours to approve.
  // Only its email validation text is kept; any other failure reads the
  // approved line that points to email or text.
  const shownError = error && normalizeEmailAddress(email) ? FALLBACK_COPY.failed : error

  function handleSubmit(e: React.FormEvent) {
    submit(e, email, (trimmedEmail) => {
      const page = `${window.location.pathname}${window.location.search}`
      const choice = describeChoice({ offerTitle, plan, price, amount })
      const message =
        `Checkout was unavailable, so this buyer asked us to finish the order.\n` +
        `Wants to buy: ${choice}\n` +
        `Checkout page: ${page}` +
        (note.trim() ? `\n\nTheir note: ${note.trim()}` : "")
      return { name: name.trim(), email: trimmedEmail, message }
    })
  }

  const fieldClass = marketingFieldClass({ className: "w-full px-4 py-3" })

  return (
    <section
      aria-labelledby={headingId}
      className="mx-auto flex w-full max-w-[34rem] flex-col gap-5 rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-white p-6 text-left md:p-7"
    >
      <div className="flex flex-col gap-2">
        <h2
          id={headingId}
          className="serious-display text-[clamp(1.3rem,2.2vw,1.6rem)] leading-tight text-[var(--color-ink)]"
        >
          {FALLBACK_COPY.heading}
        </h2>
        <p className="text-[0.92rem] leading-[1.55] text-[var(--color-ink-muted)]">{FALLBACK_COPY.lead}</p>
        {orderLine ? (
          <p className="text-[0.92rem] leading-[1.55] text-[var(--color-ink)]">
            {FALLBACK_COPY.order} {orderLine}
          </p>
        ) : null}
      </div>

      {done ? (
        <MarketingAlert role="status" variant="success">
          {FALLBACK_COPY.sent}
        </MarketingAlert>
      ) : (
        <form method="post" onSubmit={handleSubmit} className="relative flex flex-col gap-3">
          <HoneypotField />
          <input
            type="text"
            name="name"
            required
            maxLength={256}
            aria-label={FALLBACK_COPY.nameLabel}
            placeholder={FALLBACK_COPY.nameLabel}
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldClass}
          />
          <input
            type="email"
            name="email"
            required
            maxLength={254}
            inputMode="email"
            aria-label={FALLBACK_COPY.emailLabel}
            placeholder={FALLBACK_COPY.emailLabel}
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={fieldClass}
          />
          <textarea
            name="note"
            maxLength={3000}
            rows={3}
            aria-label={FALLBACK_COPY.noteLabel}
            placeholder={FALLBACK_COPY.noteLabel}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={fieldClass}
          />
          <MarketingButton type="submit" variant="accent" size="lg" disabled={state === "loading"}>
            {state === "loading" ? FALLBACK_COPY.sending : FALLBACK_COPY.submit}
          </MarketingButton>
          {shownError ? <MarketingAlert className="px-3 py-2 text-[0.85rem]">{shownError}</MarketingAlert> : null}
        </form>
      )}

      <p className="text-[0.85rem] leading-[1.5] text-[var(--color-ink-muted)]">
        {showEmail ? (
          <>
            {FALLBACK_COPY.directLead} {FALLBACK_COPY.emailWord}{" "}
            <a href={`mailto:${SITE_CONFIG.supportEmail}`} className={LINK_CLASS}>
              {SITE_CONFIG.supportEmail}
            </a>{" "}
            {FALLBACK_COPY.or} {FALLBACK_COPY.textWord}{" "}
          </>
        ) : (
          <>{FALLBACK_COPY.textOnly} </>
        )}
        <a href={`sms:${SITE_CONFIG.phone}`} className={`${LINK_CLASS} whitespace-nowrap`}>
          {displayPhone(SITE_CONFIG.phone)}
        </a>
        .
      </p>
    </section>
  )
}

// Invoice recovery: where the server has the PayPal invoice route switched on,
// the panel asks only for an email (and an optional mobile number for one text)
// and answers with the invoice link at once. It never takes card details and
// never prices anything: the server builds the order from the plan or amount in
// the URL. Any answer other than the route's own 200 (flag off, error, network
// failure) leaves the details form above exactly as it was.
//
// Every visible string lives in INVOICE_RECOVERY_COPY so they can be approved
// at once.

export const INVOICE_RECOVERY_COPY = {
  heading: "Pay by invoice",
  lead: "Enter your email and we will send you a PayPal invoice for this order.",
  emailLabel: "Email",
  phoneLabel: "US or Canada mobile number (optional)",
  smsConsent: "Also text me the invoice link, one message. Message and data rates may apply.",
  submit: "Send my invoice",
  sending: "Sending…",
  // The same terms line every other way to pay shows (payment-methods.tsx).
  termsLead: "By paying you agree to the",
  termsLink: "Terms & Conditions",
  refundLink: "Refund Policy",
  disclaimerLink: "Success & Results Disclaimer",
  privacyLink: "Privacy Policy",
  and: "and",
  emailInvalid: "Enter a valid email address.",
  invoiceHeading: "Your invoice is ready",
  invoiceEmailed: "We emailed the invoice to",
  invoiceOpen: "Open your invoice",
  textSent: "We also texted you the link.",
  amountLead: "Amount due:",
  invoiceIdLead: "Invoice ID:",
  paid: "This order is already paid. There is nothing more to do.",
  hostedHeading: "Pay on PayPal",
  hostedLead: "We could not make your invoice just now. You can pay on PayPal's payment page instead.",
  hostedAmountLead: "Amount to pay:",
  hostedIdLead: "When PayPal asks for an Invoice ID, type:",
  hostedOpen: "Open the PayPal payment page",
} as const

type InvoiceRecoveryResult =
  | { state: "invoice"; invoiceUrl: string | null; shortRef: string; amountCents: number; currency: string; text: string }
  | { state: "already_paid" }
  | { state: "hosted_link"; hostedUrl: string; shortRef: string; amountCents: number; currency: string }
  | { state: "unavailable" }

const ONES = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven",
  "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen",
]
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"]

function wordsBelowThousand(n: number): string {
  const parts: string[] = []
  const hundreds = Math.floor(n / 100)
  const rest = n % 100
  if (hundreds > 0) parts.push(`${ONES[hundreds]} hundred`)
  if (rest >= 20) parts.push(rest % 10 ? `${TENS[Math.floor(rest / 10)]}-${ONES[rest % 10]}` : TENS[Math.floor(rest / 10)])
  else if (rest > 0 || parts.length === 0) parts.push(ONES[rest])
  return parts.join(" ")
}

/** 300000 cents in usd as "three thousand dollars"; other currencies read as
 *  the plain code so a figure is never mislabelled. */
export function amountInWords(amountCents: number, currency: string): string {
  if (!Number.isInteger(amountCents) || amountCents < 0 || amountCents >= 1_000_000_000) return ""
  if (currency.toLowerCase() !== "usd") return `${(amountCents / 100).toFixed(2)} ${currency.toUpperCase()}`
  const dollars = Math.floor(amountCents / 100)
  const cents = amountCents % 100
  const groups: string[] = []
  for (const [size, name] of [[1_000_000, "million"], [1_000, "thousand"]] as const) {
    const n = Math.floor(dollars / size) % 1000
    if (n > 0) groups.push(`${wordsBelowThousand(n)} ${name}`)
  }
  const low = dollars % 1000
  if (low > 0 || groups.length === 0) groups.push(wordsBelowThousand(low))
  const dollarText = `${groups.join(" ")} ${dollars === 1 ? "dollar" : "dollars"}`
  return cents > 0 ? `${dollarText} and ${wordsBelowThousand(cents)} ${cents === 1 ? "cent" : "cents"}` : dollarText
}

function isPayPalHttps(url: string | null): url is string {
  if (!url) return false
  try {
    const u = new URL(url)
    return u.protocol === "https:" && (u.hostname === "paypal.com" || u.hostname.endsWith(".paypal.com"))
  } catch {
    return false
  }
}

/** The policy link style of the payment panel's terms line (payment-methods.tsx). */
const POLICY_LINK_CLASS =
  "inline min-h-0 border-0 p-0 text-[var(--color-ink)] underline hover:bg-transparent hover:text-[var(--color-vermilion)]"

const GET_TIMEOUT_MS = 4000
const POST_TIMEOUT_MS = 30000

type FallbackProps = {
  offerTitle: string | null
  plan: string | null
  price: string | null
  amount: string | null
  showEmail?: boolean
  sent?: boolean
  onSent?: () => void
  /** The email the page already knows, so the buyer does not retype it. */
  prefillEmail?: string | null
  /** A recurring plan. The invoice route never invoices one (an invoice is a
   *  one-time payment), so the panel is skipped and the details form shows. */
  recurring?: boolean
}

/** "checking" until the route answers; "off" is every answer that is not an
 *  explicit enabled:true, including a timeout. */
function useInvoiceRecoveryEnabled(skip: boolean): "checking" | "on" | "off" {
  const [status, setStatus] = useState<"checking" | "on" | "off">(skip ? "off" : "checking")
  useEffect(() => {
    if (skip) return
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), GET_TIMEOUT_MS)
    fetch("/api/checkout/invoice-recovery", { signal: controller.signal, cache: "no-store" })
      .then(async (res) => {
        if (res.status !== 200) return false
        const body = (await res.json()) as { enabled?: unknown }
        return body.enabled === true
      })
      .catch(() => false)
      .then((enabled) => setStatus(enabled ? "on" : "off"))
      .finally(() => clearTimeout(timer))
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [skip])
  return status
}

function InvoiceRecoveryPanel({
  plan,
  price,
  amount,
  prefillEmail,
  onUnavailable,
}: FallbackProps & { onUnavailable: () => void }) {
  const headingId = useId()
  const [email, setEmail] = useState(prefillEmail ?? "")
  const [phone, setPhone] = useState("")
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [invalid, setInvalid] = useState(false)
  const [result, setResult] = useState<InvoiceRecoveryResult | null>(null)
  const C = INVOICE_RECOVERY_COPY

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    // Read before any await: the hidden trap field's value is taken from the form.
    const form = e.currentTarget
    if (submitting) return
    const trimmed = normalizeEmailAddress(email)
    if (!trimmed) {
      setInvalid(true)
      return
    }
    setInvalid(false)
    setSubmitting(true)
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), POST_TIMEOUT_MS)
    try {
      const res = await fetch("/api/checkout/invoice-recovery", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify(
          withHoneypot(
            {
              planId: plan ?? undefined,
              price: price ?? undefined,
              amount: amount ?? undefined,
              email: trimmed,
              phone: phone.trim() || undefined,
              smsConsent: Boolean(phone.trim()) && consent,
              weekend: weekendQueryFromUrl(),
              renews: renewsQueryFromUrl(),
              acquisition_context: readAcquisitionContext() ?? undefined,
            },
            form,
          ),
        ),
      })
      const body = res.status === 200 ? ((await res.json()) as { ok?: boolean; result?: InvoiceRecoveryResult }) : null
      if (!body?.ok || !body.result || body.result.state === "unavailable") {
        onUnavailable()
        return
      }
      setResult(body.result)
    } catch {
      onUnavailable()
    } finally {
      clearTimeout(timer)
      setSubmitting(false)
    }
  }

  const fieldClass = marketingFieldClass({ className: "w-full px-4 py-3" })
  const shownEmail = normalizeEmailAddress(email) ?? ""

  return (
    <section
      aria-labelledby={headingId}
      className="mx-auto flex w-full max-w-[34rem] flex-col gap-5 rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-white p-6 text-left md:p-7"
    >
      <h2
        id={headingId}
        className="serious-display text-[clamp(1.3rem,2.2vw,1.6rem)] leading-tight text-[var(--color-ink)]"
      >
        {result?.state === "invoice"
          ? C.invoiceHeading
          : result?.state === "hosted_link"
            ? C.hostedHeading
            : C.heading}
      </h2>

      {result?.state === "already_paid" ? (
        <MarketingAlert role="status" variant="success">
          {C.paid}
        </MarketingAlert>
      ) : result?.state === "invoice" ? (
        <div className="flex flex-col gap-3" role="status">
          <p className="text-[0.95rem] leading-[1.55] text-[var(--color-ink)]">
            {C.amountLead} {formatAmount(result.amountCents, result.currency)}
          </p>
          <p className="text-[0.95rem] leading-[1.55] text-[var(--color-ink)]">
            {C.invoiceIdLead} {result.shortRef}
          </p>
          {isPayPalHttps(result.invoiceUrl) ? (
            <a
              href={result.invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={marketingActionClass({ variant: "accent", size: "lg" })}
            >
              {C.invoiceOpen}
            </a>
          ) : null}
          <p className="text-[0.88rem] leading-[1.5] text-[var(--color-ink-muted)]">
            {C.invoiceEmailed} {shownEmail}.{result.text === "sent" ? ` ${C.textSent}` : ""}
          </p>
        </div>
      ) : result?.state === "hosted_link" ? (
        <div className="flex flex-col gap-3" role="status">
          <p className="text-[0.92rem] leading-[1.55] text-[var(--color-ink-muted)]">{C.hostedLead}</p>
          <p className="text-[0.95rem] leading-[1.55] text-[var(--color-ink)]">
            {C.hostedAmountLead} {formatAmount(result.amountCents, result.currency)} (
            {amountInWords(result.amountCents, result.currency)})
          </p>
          <p className="text-[0.95rem] leading-[1.55] text-[var(--color-ink)]">
            {C.hostedIdLead} <strong>{result.shortRef}</strong>
          </p>
          {isPayPalHttps(result.hostedUrl) ? (
            <a
              href={result.hostedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={marketingActionClass({ variant: "accent", size: "lg" })}
            >
              {C.hostedOpen}
            </a>
          ) : null}
        </div>
      ) : (
        <form method="post" onSubmit={handleSubmit} noValidate className="relative flex flex-col gap-3">
          <HoneypotField />
          <p className="text-[0.92rem] leading-[1.55] text-[var(--color-ink-muted)]">{C.lead}</p>
          <input
            type="email"
            name="email"
            required
            maxLength={254}
            inputMode="email"
            aria-label={C.emailLabel}
            placeholder={C.emailLabel}
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={fieldClass}
          />
          <input
            type="tel"
            name="phone"
            maxLength={32}
            inputMode="tel"
            aria-label={C.phoneLabel}
            placeholder={C.phoneLabel}
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={fieldClass}
          />
          <label className="flex items-start gap-2 text-[0.85rem] leading-[1.5] text-[var(--color-ink-muted)]">
            <input
              type="checkbox"
              name="smsConsent"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1"
            />
            <span>{C.smsConsent}</span>
          </label>
          <MarketingButton type="submit" variant="accent" size="lg" disabled={submitting}>
            {submitting ? C.sending : C.submit}
          </MarketingButton>
          <p className="self-center text-center text-[0.8rem] leading-[1.5] text-[var(--color-ink-muted)]">
            {C.termsLead}{" "}
            <MarketingLink href="/terms-and-conditions" variant="ghost" size="sm" className={POLICY_LINK_CLASS}>
              {C.termsLink}
            </MarketingLink>
            ,{" "}
            <MarketingLink href="/refund-policy" variant="ghost" size="sm" className={POLICY_LINK_CLASS}>
              {C.refundLink}
            </MarketingLink>
            ,{" "}
            <MarketingLink href="/success-disclaimer" variant="ghost" size="sm" className={POLICY_LINK_CLASS}>
              {C.disclaimerLink}
            </MarketingLink>{" "}
            {C.and}{" "}
            <MarketingLink href="/privacy-policy" variant="ghost" size="sm" className={POLICY_LINK_CLASS}>
              {C.privacyLink}
            </MarketingLink>
            .
          </p>
          {invalid ? <MarketingAlert className="px-3 py-2 text-[0.85rem]">{C.emailInvalid}</MarketingAlert> : null}
        </form>
      )}
    </section>
  )
}

export function CheckoutFallback(props: FallbackProps) {
  // Once the details were already sent the buyer has their answer; do not ask
  // the route again on a remount. A recurring plan is never invoiced, so the
  // route is not asked for one either.
  const flag = useInvoiceRecoveryEnabled(Boolean(props.sent) || Boolean(props.recurring))
  const [declined, setDeclined] = useState(false)
  if (flag === "checking") return null
  if (flag === "on" && !declined) {
    return <InvoiceRecoveryPanel {...props} onUnavailable={() => setDeclined(true)} />
  }
  return <DetailsFallback {...props} />
}
